import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/api/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { toast } from '@/hooks/use-toast'
import { Toaster } from '@/components/ui/toaster'
import { Icons } from '@/components/ui/icons'

export default function PasswordResetPage() {
    const [loading, setLoading] = useState(false)
    const [password, setPassword] = useState('')
    const [success, setSuccess] = useState(false)
    const navigate = useNavigate()

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!password) {
            toast({
                title: "Password required",
                description: "Please enter a new password",
                variant: "destructive"
            })
            return
        }

        try {
            setLoading(true)
            const { error: updateError } = await supabase.auth.updateUser({
                password: password
            })

            if (updateError) throw updateError

            toast({
                title: "Password updated",
                description: "Your password has been updated successfully.",
            })
            setSuccess(true)

        } catch (error) {
            console.error('Password reset error:', error)
            toast({
                title: "Error resetting password",
                description: error instanceof Error ? error.message : "An error occurred while resetting your password",
                variant: "destructive"
            })
        } finally {
            setLoading(false)
        }
    }

    if (success) {
        return (
            <div className="container flex items-center justify-center min-h-[100dvh] px-4 py-8 md:py-12">
                <Card className="w-full max-w-[380px] p-4 md:p-8">
                    <div className="space-y-4 text-center">
                        <div className="flex justify-center">
                            <Icons.check className="h-12 w-12 text-green-600" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-xl md:text-2xl font-semibold text-green-600">Password Updated!</h1>
                            <p className="text-sm md:text-base text-muted-foreground">
                                Your password has been updated successfully. You can now log in with your new password in the app.
                            </p>
                        </div>
                    </div>
                    <div className="space-y-3 mt-6">
                        <a href="https://milekeeper.ai?utm_source=password-reset" target="_blank" rel="noopener noreferrer">
                            Learn More About MileKeeper
                        </a>
                    </div>
                </Card>
            </div>
        )
    }

    return (
        <>
            <div className="container flex items-center justify-center min-h-[100dvh] px-4 py-8 md:py-12">
                <Card className="w-full max-w-[380px] p-4 md:p-8">
                    <div className="space-y-4 text-center">
                        <div className="flex justify-center">
                            <Icons.lock className="h-12 w-12 text-primary" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-xl md:text-2xl font-semibold">Reset Password</h1>
                            <p className="text-sm md:text-base text-muted-foreground">
                                Please enter your new password below
                            </p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                        <div className="space-y-2">
                            <Input
                                type="password"
                                placeholder="Enter new password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            size="lg"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                "Update Password"
                            )}
                        </Button>
                    </form>
                </Card>
            </div>
            <Toaster />
        </>
    )
}
